import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import AddIcon from '@mui/icons-material/Add';
import { useDispatchPromise } from '../../../utils/AppUtils';
import { getContestFundingRequirements } from '../../../redux/contest/actions';
import type { IContestFundingRequirements } from '../../../redux/contest/types';
import { formatUsdFromCents } from '../contestWizard/fundingUtils';
import { midnight } from '../contestWizard/midnightStyles';
import { BRAND_LUXE_PRIMARY } from '../brandDashboardTokens';

export interface ContestFundSummaryBarProps {
    contestId: string;
    /** Fallback when requirements have not loaded yet (contest.amount_available). */
    fallbackFundedCents?: number;
    onAddFunds: () => void;
    /** Bump to refetch after a successful top-up. */
    refreshKey?: number;
    /** Also refetch when awards / required totals change (e.g. after editing a prize). */
    revisionKey?: string | number;
}

/**
 * Shows locked contest escrow + Add more — used on manage drawers where awards can exceed funds.
 */
export function ContestFundSummaryBar({
    contestId,
    fallbackFundedCents = 0,
    onAddFunds,
    refreshKey = 0,
    revisionKey = '',
}: ContestFundSummaryBarProps) {
    const dispatchPromise = useDispatchPromise();
    const [requirements, setRequirements] = useState<IContestFundingRequirements | null>(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        if (!contestId) return;
        setLoading(true);
        try {
            const res = (await dispatchPromise(
                getContestFundingRequirements(contestId),
            )) as IContestFundingRequirements;
            setRequirements(res);
        } catch {
            setRequirements(null);
        } finally {
            setLoading(false);
        }
    }, [contestId, dispatchPromise]);

    useEffect(() => {
        void load();
    }, [load, refreshKey, revisionKey]);

    const fundedCents = requirements?.funded_cents ?? fallbackFundedCents ?? 0;
    const spentCents = Number(requirements?.spent_cents) || 0;
    const requiredTotalCents = requirements?.required_total_cents;
    const outstandingCents =
        typeof requirements?.outstanding_required_cents === 'number'
            ? requirements.outstanding_required_cents
            : typeof requiredTotalCents === 'number'
            ? Math.max(0, requiredTotalCents - spentCents)
            : undefined;
    const shortfall =
        typeof requirements?.funding_shortfall_cents === 'number'
            ? Math.max(0, requirements.funding_shortfall_cents)
            : typeof outstandingCents === 'number' && outstandingCents > fundedCents
            ? outstandingCents - fundedCents
            : 0;
    const displayRequiredCents = typeof outstandingCents === 'number' ? outstandingCents : requiredTotalCents;

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                px: 2,
                py: 1.5,
                borderRadius: '12px',
                border: `1px solid ${shortfall > 0 ? 'rgba(255, 120, 80, 0.45)' : 'rgba(255,255,255,0.1)'}`,
                bgcolor:
                    shortfall > 0
                        ? 'rgba(255, 100, 60, 0.08)'
                        : midnight.surfaceContainerHigh || 'rgba(255,255,255,0.04)',
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                <AccountBalanceWalletOutlinedIcon sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 22 }} />
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: 11,
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            color: midnight.onSurfaceVariant,
                            fontWeight: 700,
                        }}>
                        Campaign fund
                    </Typography>
                    {loading ? (
                        <CircularProgress size={16} sx={{ color: BRAND_LUXE_PRIMARY, mt: 0.5 }} />
                    ) : (
                        <Typography sx={{ fontSize: 18, fontWeight: 700, color: midnight.onSurface, lineHeight: 1.2 }}>
                            {formatUsdFromCents(fundedCents)}
                            {typeof displayRequiredCents === 'number' && displayRequiredCents > 0 ? (
                                <Typography
                                    component="span"
                                    sx={{
                                        ml: 1,
                                        fontSize: 13,
                                        fontWeight: 500,
                                        color: midnight.onSurfaceVariant,
                                    }}>
                                    of {formatUsdFromCents(displayRequiredCents)} still needed
                                </Typography>
                            ) : null}
                        </Typography>
                    )}
                    {shortfall > 0 ? (
                        <Typography sx={{ fontSize: 12, color: '#FF8A65', mt: 0.25, fontWeight: 600 }}>
                            Shortfall {formatUsdFromCents(shortfall)} — add funds to assign awards
                        </Typography>
                    ) : null}
                </Box>
            </Box>
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onAddFunds}
                sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: '999px',
                    bgcolor: BRAND_LUXE_PRIMARY,
                    color: '#111',
                    px: 2,
                    '&:hover': { bgcolor: BRAND_LUXE_PRIMARY, filter: 'brightness(1.08)' },
                }}>
                Add more
            </Button>
        </Box>
    );
}
